import { useClerk, useUser as useClerkUser } from "@clerk/clerk-react";
import Avatar from "../../../ui/Avatar/Avatar";
import styles from "./User.module.scss";
import Dropdown from "../../../ui/Dropdown/Dropdown";
import urls from "../../../routes";
import {
  ChevronDown,
  Eye,
  LogOut,
  Settings,
  User as UserIcon,
  Users,
  UsersPlus,
} from "../../../ui/Icon/Icon";
import useUser from "../../../providers/UserProvider/useUser";
import TeamCard from "../../TeamCard/TeamCard";
import { graphql } from "../../../__generated__/gql";
import { useQuery } from "urql";
import Truncate from "../../../ui/Truncate/Truncate";
import { useDisclosure } from "../../../ui/util/useDisclosure";
import CreateNewTeamModal from "./CreateNewTeamModal/CreateNewTeamModal";
import TeamSelector from "../../TeamSelector/TeamSelector";

const ADMIN_EMAILS = ["russ@macawhq.com", "tsering@macawhq.com"];

const UserComponentQuery = graphql(/* GraphQL */ `
  query userComponent {
    ...TeamSelectorQuery
    memberships {
      id
    }
  }
`);

function UserDropdown({ children }: { children: React.ReactNode }) {
  const { tenant, billingInfo, signOut, user } = useUser();
  const { setActive, user: clerkUser } = useClerk();
  const [createNewTeamModalOpened, createNewTeamModal] = useDisclosure();
  const [userComponentQueryResponse] = useQuery({ query: UserComponentQuery });

  if (!tenant || !clerkUser) {
    return null;
  }

  return (
    <>
      <Dropdown
        classNames={{ menu: styles.dropdown }}
        asChild
        withinPortal
        placement="top-start"
        offsetAmount={16}
        header={
          userComponentQueryResponse.data &&
          userComponentQueryResponse.data.memberships.length > 1 ? (
            <div className={styles.teamSelector}>
              <TeamSelector
                data={userComponentQueryResponse.data}
                onClick={async (team) => {
                  localStorage.setItem(
                    "previousOrganizationId",
                    tenant?.identityId ?? "",
                  );
                  await setActive({
                    organization: team.identityId,
                  });

                  window.location.href = urls.home;
                }}
                filter={(team) => team.id !== tenant.id}
              >
                <TeamCard
                  name={tenant.name}
                  memberCount={tenant.memberCount}
                  billingTier={billingInfo.billingTier}
                />
              </TeamSelector>
            </div>
          ) : (
            <div className={styles.teamSelector}>
              <div className={styles.teamSelectorNoDropdown}>
                <TeamCard
                  name={tenant.name}
                  memberCount={tenant.memberCount}
                  billingTier={billingInfo.billingTier}
                />
              </div>
            </div>
          )
        }
        items={[
          {
            items: [
              {
                label: "Add team members",
                IconComponent: Users,
                href: urls.settings.team,
              },
              {
                label: "Team settings",
                IconComponent: Settings,
                href: urls.settings.team,
              },
              {
                label: "Create new team",
                IconComponent: UsersPlus,
                onClick: createNewTeamModal.open,
              },
              ...(user?.email && ADMIN_EMAILS.includes(user.email)
                ? [
                    {
                      label: "Admin panel",
                      IconComponent: Eye,
                      href: urls.admin.root,
                    },
                  ]
                : []),
              <div className={styles.user} key="user">
                <Avatar
                  className={styles.avatar}
                  imageUrl={clerkUser.imageUrl}
                  hideTooltip
                />
                <div className={styles.textContainer}>
                  <Truncate className={styles.name}>
                    {clerkUser.fullName}
                  </Truncate>
                  <Truncate className={styles.email}>
                    {clerkUser.primaryEmailAddress?.emailAddress}
                  </Truncate>
                </div>
              </div>,
              {
                label: "Update profile",
                IconComponent: UserIcon,
                href: urls.settings.profile.root,
              },
              {
                label: "Log out",
                IconComponent: LogOut,
                onClick: () => signOut(),
              },
            ],
          },
        ]}
      >
        {children}
      </Dropdown>

      <CreateNewTeamModal
        isOpen={createNewTeamModalOpened}
        onClose={createNewTeamModal.close}
      />
    </>
  );
}

function User() {
  const { user } = useClerkUser();
  const { tenant } = useUser();

  if (!user || !tenant) {
    return null;
  }

  return (
    <UserDropdown>
      <div className={styles.button}>
        <div className={styles.initial}>
          {tenant.name.charAt(0).toUpperCase()}
        </div>
        <div className={styles.details}>
          <div className={styles.teamName}>
            <Truncate>{tenant.name}</Truncate>
            <ChevronDown size="small" />
          </div>
          <Truncate className={styles.userEmail}>
            {user.primaryEmailAddress?.emailAddress}
          </Truncate>
        </div>
      </div>
    </UserDropdown>
  );
}

export default User;
